import { ENUM_ClientUserCreationMethod } from '@api-new/common';
import { createSelector } from '@ngrx/store';
import { DashboardWidget } from '@platform/dashboard/models/dashboard-news-banner.model';
import { selectPortfolioPropertyList } from '@platform/portfolio/selectors/portfolio-property.selector';
import { selectBranding } from '@shared/store/auth/auth.selectors';
import { selectClientUser } from '@shared/store/client-user/client-user.selectors';
import { selectCanAccessSmartSearch } from '@shared/store/company/company.selectors';
import {
  addActiveMortgageCaseWidget,
  addBestDealWidget,
  addEmptyAccountWidgets,
  addEpcImprovementsWidget,
  addLandlordWidgets,
  addMortgageWidgetsIfExpiringSoon,
  addMortgageWithoutDealWidget,
  addSmartSearchWidget,
} from './utils/dashboard-widget-selector.util';

export const selectDashboardWidgets = createSelector(
  selectClientUser,
  selectPortfolioPropertyList,
  selectCanAccessSmartSearch,
  selectBranding,
  (clientUser, portfolioPropertyList, canAccessSmartSearch, branding): DashboardWidget[] => {
    if (!clientUser || !portfolioPropertyList) return [];

    const dashboardWidgets: DashboardWidget[] = [];

    let hasProperty = false;
    let hasMortgage = false;
    let hasBetterDeal = false;
    let hasExpirationWidget = false;
    let numberOfClientProperties = 0;

    // Iterate portfolio items
    portfolioPropertyList.forEach((portfolioItem) => {
      hasProperty = hasProperty || !!portfolioItem.property;
      hasMortgage = hasMortgage || !!portfolioItem.mortgage;
      hasBetterDeal = hasBetterDeal || !!portfolioItem.bestBetterDeal;

      if (portfolioItem.property) numberOfClientProperties++;

      if (portfolioItem.mortgageCase) {
        addActiveMortgageCaseWidget(portfolioItem, dashboardWidgets);
      } else if (portfolioItem.bestBetterDeal) {
        addBestDealWidget(portfolioItem, dashboardWidgets);
      } else if (
        portfolioItem?.mortgage?.numberOfMonthsToLastEarlyRepaymentChargeEndDate &&
        portfolioItem.mortgage.numberOfMonthsToLastEarlyRepaymentChargeEndDate <= 9
      ) {
        if (!hasExpirationWidget) {
          hasExpirationWidget = true;
          addMortgageWidgetsIfExpiringSoon(portfolioItem, dashboardWidgets);
        }
      }

      addEpcImprovementsWidget(portfolioItem, dashboardWidgets);
    });

    // Add a widget if there's a mortgage but no best deal, mortgage is not expiring
    if (!hasBetterDeal && hasMortgage && !hasExpirationWidget) {
      addMortgageWithoutDealWidget(dashboardWidgets, clientUser);
    }

    // Add landlord-specific widgets
    if (clientUser.creationMethod === ENUM_ClientUserCreationMethod.CLIENT_USER_CREATION_METHOD_SIGN_UP_LANDLORD) {
      addLandlordWidgets(clientUser, numberOfClientProperties, dashboardWidgets);
    }

    // Add empty account widgets - if is EMPTY
    if (!hasProperty && !hasBetterDeal && !hasMortgage) addEmptyAccountWidgets(dashboardWidgets);

    // Add smart search widget
    if (canAccessSmartSearch) {
      addSmartSearchWidget(dashboardWidgets);
    }

    dashboardWidgets.map((widget) => (widget.isCompanyBranded = !!branding?.cssFileUrl && !!branding?.imageFolderUrl));

    return dashboardWidgets.sort((a, b) => +a.type - +b.type);
  },
);
